import { useState, useCallback, useEffect, useMemo } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore } from '../../state/store';
import { generateId } from '../../utils/helpers';
import { GROUND_PLANE } from '../../core/scene';

/**
 * NURBS Curve Tool - 3D-style curve drawing
 * 
 * Replicates 3D's Curve command:
 * - Click to place control points
 * - Shows control polygon and smooth curve preview
 * - Enter/Escape to finish
 * - Supports degree 2 (quadratic) and 3 (cubic)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCurveTool() {
    const [controlPoints, setControlPoints] = useState<THREE.Vector3[]>([]);
    const [currentPoint, setCurrentPoint] = useState<THREE.Vector3 | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [degree, setDegree] = useState(3); // Cubic by default

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();
    const isActive = activeTool === 'curve';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    /**
     * Calculate points on a B-spline curve
     */
    const calculateCurvePoints = useCallback((points: THREE.Vector3[], deg: number, numSamples: number = 50): THREE.Vector3[] => {
        if (points.length < deg + 1) return points;

        // Create a CatmullRomCurve3 as a simple B-spline approximation
        const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.5);
        return curve.getPoints(numSamples);
    }, []);

    /**
     * Create NURBS curve entity
     */
    const createCurve = useCallback((points: THREE.Vector3[]) => {
        if (points.length < 2) return;

        addEntity({
            id: generateId(),
            type: 'curve',
            name: `Curve-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                controlPoints: points.map(p => [p.x, p.y, p.z]),
                degree,
                color: '#00ff00',
            },
        });
    }, [addEntity, activeLayerId, degree]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        setControlPoints([...controlPoints, point.clone()]);
        setIsDrawing(true);
    }, [isActive, controlPoints, getGroundPoint]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            // Sub-pixel jitter dedupe: identical reads keep the previous
            // reference so React skips the CurvePreview reconciliation.
            setCurrentPoint((prev) => (prev && prev.distanceToSquared(point) < 1e-8 ? prev : point));
        }
    }, [isActive, getGroundPoint]);

    /**
     * Finish curve on Enter, cancel on Escape
     */
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && controlPoints.length >= 2) {
                createCurve(controlPoints);
                setControlPoints([]);
                setCurrentPoint(null);
                setIsDrawing(false);
            }
            if (e.key === 'Escape') {
                setControlPoints([]);
                setCurrentPoint(null);
                setIsDrawing(false);
            }
            if (e.key === 'Backspace' && controlPoints.length > 0) {
                setControlPoints(controlPoints.slice(0, -1));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [controlPoints, createCurve]);

    return {
        isActive,
        isDrawing,
        controlPoints,
        currentPoint,
        degree,
        setDegree,
        calculateCurvePoints,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Curve Preview Component
 */
export function CurvePreview({
    controlPoints,
    currentPoint,
    degree,
}: {
    controlPoints: THREE.Vector3[];
    currentPoint: THREE.Vector3 | null;
    degree: number;
}) {
    if (controlPoints.length === 0) return null;

    // Add current point to preview
    const allPoints = currentPoint
        ? [...controlPoints, currentPoint]
        : controlPoints;

    // Calculate smooth curve
    let curvePoints: THREE.Vector3[] = allPoints;
    if (allPoints.length >= degree + 1) {
        const curve = new THREE.CatmullRomCurve3(allPoints, false, 'catmullrom', 0.5);
        curvePoints = curve.getPoints(50);
    }

    return (
        <group>
            {/* Control polygon */}
            <Line
                points={allPoints}
                color="#888888"
                lineWidth={1}
                dashed
                dashSize={0.1}
                gapSize={0.05}
            />

            {/* Smooth curve */}
            <Line
                points={curvePoints}
                color="#00ff00"
                lineWidth={2}
            />

            {/* Control points */}
            {allPoints.map((point, i) => (
                <mesh key={i} position={point}>
                    <sphereGeometry args={[0.08, 16, 16]} />
                    <meshBasicMaterial color={i === allPoints.length - 1 ? '#ffff00' : '#00ff00'} />
                </mesh>
            ))}
        </group>
    );
}

/**
 * Surface Tool - 3D-style surface from curves
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSurfaceTool() {
    const [curves] = useState<string[]>([]);
    const [surfaceType, setSurfaceType] = useState<'loft' | 'extrude' | 'revolve'>('loft');

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const entities = useAppStore((s) => s.entities);

    const isActive = activeTool === 'surface';

    /**
     * Create surface from selected curves
     */
    const createSurface = useCallback(() => {
        const selectedCurves = selectedIds
            .map(id => entities[id])
            .filter(e => e?.type === 'curve');

        if (selectedCurves.length < 2) return null;

        const id = generateId();
        addEntity({
            id,
            type: 'surface',
            name: `Surface-${id.slice(0, 6)}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                curveIds: selectedCurves.map(c => c.id),
                surfaceType,
                color: '#808080',
            },
        });

        return id;
    }, [addEntity, activeLayerId, selectedIds, entities, surfaceType]);

    return {
        isActive,
        curves,
        surfaceType,
        setSurfaceType,
        createSurface,
    };
}

/**
 * Box Tool - 3D primitive creation
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBoxTool() {
    const [corner1, setCorner1] = useState<THREE.Vector3 | null>(null);
    const [corner2, setCorner2] = useState<THREE.Vector3 | null>(null);
    const [height, setHeight] = useState<number>(0);
    const [step, setStep] = useState<'base' | 'height'>('base');
    const [isDrawing, setIsDrawing] = useState(false);

    const addEntity = useAppStore((s) => s.addEntity);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();
    const isActive = activeTool === 'box';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const createBox = useCallback((c1: THREE.Vector3, c2: THREE.Vector3, h: number) => {
        const width = Math.abs(c2.x - c1.x);
        const depth = Math.abs(c2.y - c1.y);
        const centerX = (c1.x + c2.x) / 2;
        const centerY = (c1.y + c2.y) / 2;

        addEntity({
            id: generateId(),
            type: 'box',
            name: `Box-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                position: [centerX, centerY, h / 2],
                size: [width, depth, h],
                color: '#808080',
            },
        });
    }, [addEntity, activeLayerId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        if (step === 'base') {
            if (!isDrawing) {
                setCorner1(point);
                setCorner2(point);
                setIsDrawing(true);
            } else {
                setCorner2(point);
                setStep('height');
            }
        } else if (step === 'height' && corner1 && corner2) {
            createBox(corner1, corner2, height || 1);
            setCorner1(null);
            setCorner2(null);
            setHeight(0);
            setStep('base');
            setIsDrawing(false);
        }
    }, [isActive, step, isDrawing, corner1, corner2, height, getGroundPoint, createBox]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !isDrawing) return;

        const point = getGroundPoint();
        if (!point) return;

        if (step === 'base') {
            setCorner2((prev) => (prev && prev.distanceToSquared(point) < 1e-8 ? prev : point));
        } else if (step === 'height' && corner1) {
            // Use vertical distance for height
            const dist = point.distanceTo(corner1);
            setHeight((prev) => (Math.abs(prev - dist) < 1e-4 ? prev : Math.max(0.1, dist)));
        }
    }, [isActive, isDrawing, step, corner1, getGroundPoint]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setCorner1(null);
                setCorner2(null);
                setHeight(0);
                setStep('base');
                setIsDrawing(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return {
        isActive,
        isDrawing,
        corner1,
        corner2,
        height,
        step,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Box Preview Component
 */
export function BoxPreview({
    corner1,
    corner2,
    height
}: {
    corner1: THREE.Vector3;
    corner2: THREE.Vector3;
    height: number;
}) {
    const width = Math.abs(corner2.x - corner1.x);
    const depth = Math.abs(corner2.y - corner1.y);
    const centerX = (corner1.x + corner2.x) / 2;
    const centerY = (corner1.y + corner2.y) / 2;
    const h = height || 0.1;

    return (
        <group position={[centerX, centerY, h / 2]}>
            <mesh>
                <boxGeometry args={[width || 0.1, depth || 0.1, h]} />
                <meshStandardMaterial color="#4a90d9" transparent opacity={0.5} />
            </mesh>
            <mesh>
                <boxGeometry args={[width || 0.1, depth || 0.1, h]} />
                <meshBasicMaterial color="#ffffff" wireframe />
            </mesh>
        </group>
    );
}
